import type { BatchPersonField, BatchPersonRawRow } from "./types";
import { REQUIRE_BATCH_PERSON_BIRTH_DATE } from "./batchPersonRegisterFlags";
import { inferMissingColumnsFromSamples } from "./inferMissingColumnsFromSamples";

export const BATCH_PERSON_FIELDS: BatchPersonField[] = [
  "cpf",
  "name",
  "gender",
  "birthDate",
  "phone",
];

const REQUIRED_FIELDS: BatchPersonField[] = REQUIRE_BATCH_PERSON_BIRTH_DATE
  ? BATCH_PERSON_FIELDS
  : BATCH_PERSON_FIELDS.filter((field) => field !== "birthDate");

/** Match exato depois da normalização (prioridade máxima). */
const HEADER_ALIASES: Record<string, BatchPersonField> = {
  cpf: "cpf",
  identifier: "cpf",
  documento: "cpf",
  nome: "name",
  name: "name",
  sexo: "gender",
  sexonascimento: "gender",
  sexo_nascimento: "gender",
  gender: "gender",
  datanascimento: "birthDate",
  data_nascimento: "birthDate",
  nascimento: "birthDate",
  birthdate: "birthDate",
  telefone: "phone",
  phone: "phone",
  celular: "phone",
  whatsapp: "phone",
};

export type ColumnMapping = {
  indexes: Partial<Record<BatchPersonField, number>>;
  usedFlexibleMapping: boolean;
};

function stripAccents(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export function normalizeHeader(value: string): string {
  return stripAccents(value)
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_");
}

function tokensOf(normalized: string): string[] {
  return normalized.split("_").filter(Boolean);
}

function hasToken(normalized: string, token: string): boolean {
  return tokensOf(normalized).includes(token) || normalized.includes(token);
}

function looksLikePhoneHeader(normalized: string): boolean {
  return ["telefone", "phone", "celular", "whatsapp", "fone"].some((token) =>
    hasToken(normalized, token),
  );
}

function looksLikeDateHeader(normalized: string): boolean {
  if (normalized.includes("datanascimento") || hasToken(normalized, "birthdate")) {
    return true;
  }
  const hasBirth =
    hasToken(normalized, "nasc") ||
    hasToken(normalized, "nascimento") ||
    hasToken(normalized, "birth");
  return hasToken(normalized, "data") && hasBirth;
}

function guessFieldByName(
  normalized: string,
  mapped: Set<BatchPersonField>,
  allowMaeName: boolean,
): BatchPersonField | null {
  const exact = HEADER_ALIASES[normalized];
  if (exact && !mapped.has(exact)) return exact;

  if (!mapped.has("cpf") && hasToken(normalized, "cpf")) return "cpf";
  if (!mapped.has("phone") && looksLikePhoneHeader(normalized)) return "phone";
  if (
    !mapped.has("gender") &&
    (hasToken(normalized, "sexo") || hasToken(normalized, "gender")) &&
    !looksLikeDateHeader(normalized) &&
    !looksLikePhoneHeader(normalized)
  ) {
    return "gender";
  }
  if (!mapped.has("birthDate") && looksLikeDateHeader(normalized)) return "birthDate";
  if (!mapped.has("name") && (hasToken(normalized, "nome") || hasToken(normalized, "name"))) {
    const isMae = hasToken(normalized, "mae");
    if (!isMae || allowMaeName) return "name";
  }
  if (
    !mapped.has("cpf") &&
    (hasToken(normalized, "documento") || hasToken(normalized, "identifier"))
  ) {
    return "cpf";
  }
  return null;
}

function mapByName(headers: string[]): Partial<Record<BatchPersonField, number>> {
  const indexes: Partial<Record<BatchPersonField, number>> = {};
  const mapped = new Set<BatchPersonField>();

  const assignPass = (allowMaeName: boolean) => {
    headers.forEach((header, index) => {
      const field = guessFieldByName(normalizeHeader(header), mapped, allowMaeName);
      if (!field) return;
      indexes[field] = index;
      mapped.add(field);
    });
  };

  assignPass(false);
  if (!mapped.has("name")) assignPass(true);
  return indexes;
}

/**
 * Resolve colunas/campos equivalentes às 5 informações, independente da ordem.
 */
export function resolveColumnMapping(
  headers: string[],
  sampleRows: string[][] = [],
): ColumnMapping | null {
  if (headers.length === 0) return null;
  const indexes = mapByName(headers);
  const missingAfterName = REQUIRED_FIELDS.some((field) => indexes[field] === undefined);
  if (missingAfterName && sampleRows.length > 0) {
    inferMissingColumnsFromSamples(indexes, sampleRows);
  }
  if (REQUIRED_FIELDS.some((field) => indexes[field] === undefined)) return null;

  const usedFlexibleMapping = REQUIRED_FIELDS.some((field) => {
    const mappedIndex = indexes[field];
    if (mappedIndex === undefined) return false;
    const header = headers[mappedIndex] || "";
    return HEADER_ALIASES[normalizeHeader(header)] !== field;
  });

  return {
    indexes,
    usedFlexibleMapping,
  };
}

export function rowFromCells(
  cells: string[],
  indexes: Partial<Record<BatchPersonField, number>>,
  lineNumber: number,
): BatchPersonRawRow {
  const read = (field: BatchPersonField) => {
    const index = indexes[field];
    if (index === undefined) return "";
    return cells[index] || "";
  };
  return {
    lineNumber,
    cpf: read("cpf"),
    name: read("name"),
    gender: read("gender"),
    birthDate: read("birthDate"),
    phone: read("phone"),
  };
}
